import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from '../utils/objectUtils';
import { create, getAll, getById, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import { collection, getCountFromServer, getDocs, query, orderBy, limitToLast, limit, startAfter, endBefore, where } from 'firebase/firestore';
import { db } from '../firebase';
import { convertToSlug } from '../utils/stringFormatter';

class Survey {
	static collectionName = 'surveys';

	name = null;
	slug = convertToSlug(this.name);
	scope = null; // enum SurveyScopes
	questions = []; // Array of Questions
	active = true;

	constructor(survey) {
		if (isObjectNotEmpty(survey)) {
			if (isStringNotEmpty(survey.name)) { this.name = survey.name; }
			if (isStringNotEmpty(survey.slug)) { this.slug = survey.slug; }
			else { this.slug = convertToSlug(this.name); }
			if (isStringNotEmpty(survey.scope)) { this.scope = survey.scope; }
			if (isListNotEmpty(survey.questions)) { this.questions = survey.questions; }
			if (isBooleanTrue(survey.active)) { this.active = true; } else { this.active = false; }
		}
	}

	toDto = () => {
		return {
			name: this.name,
			slug: this.slug,
			scope: this.scope,
			questions: this.questions,
			active: this.active
		};
	}

	isValid = () => {
		if (isStringEmpty(this.name)) {
			throw new Error('name is required.');
		}

		return true;
	}

	create = async () => {
		if (this.isValid()) {
			return await create(Survey.collectionName, this.toDto());
		}
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(Survey.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(Survey.collectionName, id, updatedFields);
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedSurvey) => {
		const updatedObj = new Survey(updatedSurvey);
		if (updatedObj.isValid() && id === updatedSurvey.id) {
			return await update(Survey.collectionName, id, updatedObj.toDto());
		} else {
			throw new Error('id and id field of the updated survey must match.')
		}
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(Survey.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getById = async (id) => {
		if (isStringNotEmpty(id)) {
			return await getById(Survey.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getByName = async (name) => {
		if (isStringNotEmpty(name)) {
			return await getBySingleQuery(Survey.collectionName, 'name', name, '==');
		} else {
			throw new Error("name is required.");
		}
	}

	static getBySlug = async (slug) => {
		if (isStringNotEmpty(slug)) {
			return await getBySingleQuery(Survey.collectionName, 'slug', slug, '==');
		} else {
			throw new Error("slug is required.");
		}
	}

	static getAll = async () => {
		return await getAll(Survey.collectionName);
	}

	static getCount = async (queryParameters) => {
		const { active } = queryParameters;

		let queryConstraints = [];

		if (isBooleanTrue(active)) {
			queryConstraints.push(where('active', '==', true));
		} else if (isBooleanFalse(active)) {
			queryConstraints.push(where('active', '==', false));
		}

		let q = query(collection(db, Survey.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		const { active } = queryParameters;

		let queryConstraints = [];

		if (isBooleanTrue(active)) {
			queryConstraints.push(where('active', '==', true));
		} else if (isBooleanFalse(active)) {
			queryConstraints.push(where('active', '==', false));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collection(db, Survey.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collection(db, Survey.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
		} else {
			q = query(collection(db, Survey.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		let snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await this.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default Survey;